// Query and mutation hooks over the agent run routes (spec 2026-10-21-19 §2).
// Every query goes through $api so live-invalidation.ts's `agent-runs` entry
// matches its key.
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { $api } from '../../api/queryClient'
import { apiClient } from '../../api/client'
import type { AgentMission, AgentRun, AgentRunDecisionResult, AgentRunItemDecisionRequest } from '../../api/schema'
import { useLiveSubscription, useScopeLive } from '../../contexts/LiveEventsContext'
import { useDynamicRefresh } from '../../hooks/useDynamicRefresh'

export type MissionTarget = 'import' | 'site' | 'building' | 'floor'
export type TargetEntityType = 'import' | 'asset'

const RUNS = '/api/v1/organizations/{orgCode}/agent-runs'
const RUN = '/api/v1/organizations/{orgCode}/agent-runs/{agentRunUid}'

/** An API error body: `{code, title, fields?}`. */
export interface ApiError {
    code?: string
    title?: string
    detail?: string
    fields?: { name: string; message: string }[]
}

export function isActiveRun(run: Pick<AgentRun, 'status'>): boolean {
    return run.status === 'queued' || run.status === 'running'
}

export function useAgentMissions(org: string) {
    return $api.useQuery('get', '/api/v1/organizations/{orgCode}/agent-missions', {
        params: { path: { orgCode: org } },
    })
}

export function useAgentRuns(org: string, targetType: TargetEntityType, targetUid: string, limit = 5) {
    return $api.useQuery('get', RUNS, {
        params: {
            path: { orgCode: org },
            query: { target_entity_type: targetType, target_entity_uid: targetUid, limit },
        },
    }, { enabled: !!targetUid })
}

export function useAgentRun(org: string, uid: string) {
    return $api.useQuery('get', RUN, { params: { path: { orgCode: org, agentRunUid: uid } } }, { enabled: !!uid })
}

/** Missions offering a harness for this target. */
export function missionsFor(missions: AgentMission[] | undefined, target: MissionTarget): AgentMission[] {
    return (missions ?? []).filter((m) => m.targets.includes(target) && m.harnesses.length > 0)
}

/** Invalidates every agent run query of the organization (list, detail, events...). */
export function useInvalidateAgentRuns(org: string) {
    const queryClient = useQueryClient()
    return () =>
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]
                if (key[0] !== 'get' || typeof key[1] !== 'string') return false
                if (!(key[1] as string).startsWith(RUNS)) return false
                const params = (key[2] as { params?: { path?: { orgCode?: string } } } | undefined)?.params
                return params?.path?.orgCode === org
            },
        })
}

/** POST /agent-runs, returning the run or the API error. */
export async function launchAgentRun(
    org: string,
    body: {
        mission: string
        target_entity_type: TargetEntityType
        target_entity_uid: string
        harness: string
        model: string
        params: Record<string, unknown>
    },
): Promise<{ run?: AgentRun; error?: ApiError }> {
    const { data, error } = await apiClient.POST(RUNS, { params: { path: { orgCode: org } }, body })
    if (error || !data) return { error: (error ?? {}) as ApiError }
    return { run: data }
}

type RunAction = 'cancel' | 'approve' | 'reject'

/** POST /agent-runs/{uid}/{cancel|approve|reject}. */
export async function runAction(org: string, uid: string, action: RunAction): Promise<ApiError | undefined> {
    const path = `${RUN}/${action}` as `${typeof RUN}/cancel`
    const { error } = await (apiClient.POST as unknown as (
        p: string,
        o: { params: { path: { orgCode: string; agentRunUid: string } } },
    ) => Promise<{ error?: unknown }>)(path, { params: { path: { orgCode: org, agentRunUid: uid } } })
    return error ? ((error ?? {}) as ApiError) : undefined
}

/** The run's pending proposals, labelled for the run page (spec 2026-10-24-02). */
export function useAgentRunProposals(org: string, uid: string, enabled = true) {
    return $api.useQuery('get', `${RUN}/proposals`, { params: { path: { orgCode: org, agentRunUid: uid } } }, { enabled: !!uid && enabled })
}

/** POST /agent-runs/{uid}/proposals/decision: approve or reject one item. */
export async function decideAgentRunItem(
    org: string,
    uid: string,
    body: { entity_type: AgentRunItemDecisionRequest['entity_type']; uid: string; decision: 'approve' | 'reject' },
): Promise<{ result?: AgentRunDecisionResult; error?: ApiError }> {
    const { data, error } = await apiClient.POST(`${RUN}/proposals/decision`, {
        params: { path: { orgCode: org, agentRunUid: uid } },
        body,
    })
    if (error || !data) return { error: (error ?? {}) as ApiError }
    return { result: data }
}

/** Downloads the run's gzipped JSONL transcript. */
export async function downloadAgentRunTranscript(org: string, uid: string) {
    const { data, error } = await apiClient.GET(`${RUN}/transcript`, {
        params: { path: { orgCode: org, agentRunUid: uid } },
        parseAs: 'blob',
    })
    if (error || !data) throw new Error('transcript download failed')
    const url = URL.createObjectURL(data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${uid}.jsonl.gz`
    a.click()
    URL.revokeObjectURL(url)
}

/**
 * Keeps one run fresh: the `agent-runs` live scope of the run, and adaptive
 * polling underneath while it is queued or running
 * (docs/frontend/dynamic-refresh.md).
 */
export function useAgentRunLive(org: string, run: AgentRun | undefined, refetch: () => Promise<unknown>) {
    const uid = run?.uid ?? ''
    const active = !!run && isActiveRun(run)
    useLiveSubscription({ entity: 'agent-runs', uid }, { enabled: !!uid })
    const live = useScopeLive({ entity: 'agent-runs', uid }) && !!uid
    const dynamicRefresh = useDynamicRefresh({ enabled: active, defaultInterval: 2000, live })
    const stamp = run ? `${run.status}|${run.current_step?.code ?? ''}|${run.current_step?.done ?? ''}|${run.pending_proposals.total}` : ''

    useEffect(() => {
        if (!dynamicRefresh.isEnabled || !active) return
        if (dynamicRefresh.timeRemaining !== 0) return
        void refetch()
        // keyed on timeRemaining, the documented trigger of this hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dynamicRefresh.timeRemaining, dynamicRefresh.isEnabled, active])

    useEffect(() => {
        if (!stamp) return
        dynamicRefresh.controls.adjustRate(stamp !== dynamicRefresh.lastTimestamp, stamp)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stamp, org])
}
